import { useEffect, useState } from 'react';
import { Alert, Badge, Modal, Spinner } from 'react-bootstrap';

import { getEventSources, type EventSource, type EventSourceKind, type EventSourcesResponse } from 'api/events/endpoints/getEventSources';
import { toError } from 'helpers/toError';

import { EntityLink } from './EntityLink';
import { nodeDefinitionUrl } from './entityUrls';

import './SourcesModal.scss';

interface SourcesModalProps {
  readonly eventId: number;
  readonly onHide: () => void;
}

const GROUP_TITLES: Record<EventSourceKind, string> = {
  node_completion: 'On node completion',
  flow_node_completion: 'On a flow step completing',
  runtime_limit: 'On a runtime-limit breach',
  status_change_trigger: 'From a status-change trigger',
  file_arrival: 'On file arrival',
};

/** Kinds in the order they are worth reading, not the order they arrive. */
const GROUP_ORDER: readonly EventSourceKind[] = ['node_completion', 'flow_node_completion', 'runtime_limit', 'status_change_trigger', 'file_arrival'];

/** Empty is as absent as null for display: both render a separator with nothing after it. */
const isPresent = (value: string | null): value is string => value !== null && value !== '';

/** Joins the clauses a row actually has, so an absent one leaves no separator behind. */
export const clauses = (...parts: readonly (string | false | null)[]): string =>
  parts.filter((part): part is string => typeof part === 'string' && part !== '').join(' · ');

/** The step a raise is configured on: its id, its label, or both — whichever survived. */
export const describeStep = (id: string | null, label: string | null): string => {
  if (!isPresent(id)) return isPresent(label) ? label : '—';
  return isPresent(label) && label !== id ? `${id} — ${label}` : id;
};

/**
 * The child a step runs, qualified by its own zone when that is not the flow's.
 *
 * Same idiom as the flow editor's node library, and shown for the same reason:
 * the node library is not zone-scoped, so a flow can place a child from another
 * zone, where the bare name is ambiguous (#1030). Silent when the zones match,
 * which is the usual case.
 *
 * NOTE: the presence check belongs here rather than at the call site. Qualifying
 * an absent name composes the literal "null (Other)", and a caller that guards
 * afterwards has already built it.
 */
export const describeChild = (name: string | null, childZone: string | null, flowZone: string | null): string | null => {
  if (!isPresent(name)) return null;
  return isPresent(childZone) && childZone !== flowZone ? `${name} (${childZone})` : name;
};

/** The two clauses both completion kinds share: what gated the raise. */
const statusAndPrecondition = (source: EventSource): readonly (string | false)[] => [
  isPresent(source.status) && `on ${source.status}`,
  isPresent(source.precondition) && `if ${source.precondition}`,
];

/** The per-kind body of a row: what this particular kind of source is. */
const SourceDetail = ({ source }: { readonly source: EventSource }) => {
  switch (source.kind) {
    case 'node_completion':
      return (
        <>
          <EntityLink id={source.node_definition_id} to={nodeDefinitionUrl}>
            {source.node_name}
          </EntityLink>
          <span className="text-muted ms-2">{clauses(...statusAndPrecondition(source))}</span>
        </>
      );
    case 'flow_node_completion': {
      // The step id is what the flow editor and an operator's notes use, so it
      // leads and the label is additional. NOTE: flow_definition_nodes.flow_node_id
      // is nullable, so composing the two unconditionally renders the literal
      // "null" next to a label that did survive — name whichever is there.
      const stepLabel = describeStep(source.flow_node_id, source.flow_node_label);
      const childLabel = describeChild(source.node_name, source.child_zone_name, source.zone_name);
      return (
        <>
          <EntityLink id={source.flow_definition_id} to={nodeDefinitionUrl}>
            {source.flow_definition_name}
          </EntityLink>
          <span className="text-muted ms-2">{clauses(`step ${stepLabel}`, childLabel, ...statusAndPrecondition(source))}</span>
        </>
      );
    }
    case 'runtime_limit':
      return (
        <>
          <EntityLink id={source.node_definition_id} to={nodeDefinitionUrl}>
            {source.node_name}
          </EntityLink>
          <span className="text-muted ms-2">
            outside {source.runtime_limit_min ?? '—'}–{source.runtime_limit_max ?? '—'} min
          </span>
        </>
      );
    case 'status_change_trigger':
      return (
        <>
          <EntityLink id={source.trigger_id} to={(id) => `/automation/triggers?trigger_id=${id}`}>
            {source.trigger_name}
          </EntityLink>
          <span className="text-muted ms-2">
            {source.watched_node_name ?? 'any node'}
            {source.from_status !== null && ` from ${source.from_status}`}
            {source.to_status !== null && ` to ${source.to_status}`}
          </span>
          {/* These can watch zones other than the event's own, which is otherwise invisible. */}
          {source.monitor_all_zones === true && (
            <Badge bg="info" className="ms-2">
              all zones
            </Badge>
          )}
        </>
      );
    case 'file_arrival':
      return (
        <>
          <span>
            {source.directory ?? '—'}/{source.naming_pattern ?? '*'}
          </span>
          <span className="text-muted ms-2">worker {source.worker ?? '—'}</span>
          {/* Configured but switched off is precisely the diagnosis this modal exists for. */}
          {source.enabled === false && (
            <Badge bg="warning" className="ms-2">
              disabled
            </Badge>
          )}
        </>
      );
  }
};

/**
 * One source as a row: what it is, plus its zone when that is not the event's own.
 *
 * NOTE: the zone belongs on the row rather than in any one kind's body, because
 * four of the five kinds carry one and every one of them can be cross-zone — a
 * definition's raise row, its runtime-limit event and a trigger's target are all
 * resolved by id with no zone constraint, and a flow step's raise names its zone
 * explicitly in the placement JSON. Left inside a single case it would claim
 * flows are the only cross-zone raiser, which is untrue and misleading. Only
 * `file_arrival` reports no zone: that source *is* the event's own watcher
 * config, so it is never elsewhere, and `isPresent` skips it without a special
 * case. A trigger can show this alongside `all zones`; they say different things
 * — which zone it lives in, and which zones it watches.
 */
const SourceRow = ({ source, eventZoneName }: { readonly source: EventSource; readonly eventZoneName: string }) => (
  <li>
    <SourceDetail source={source} />
    {isPresent(source.zone_name) && source.zone_name !== eventZoneName && (
      <Badge bg="info" className="ms-2">
        zone {source.zone_name}
      </Badge>
    )}
  </li>
);

/**
 * Everything that can raise one event, grouped by kind.
 *
 * Manual raise is deliberately absent: every event can be raised by hand, so
 * listing it would tell the operator nothing and would bury the diagnosis this
 * panel exists for — an empty list means nothing feeds this event
 * automatically, which is why it is starving. The card's Raise button is the
 * manual affordance.
 */
export const SourcesModal = ({ eventId, onHide }: SourcesModalProps) => {
  const [data, setData] = useState<EventSourcesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    getEventSources(eventId)
      .then((response) => {
        if (current) setData(response);
      })
      .catch((loadError) => {
        if (current) setError(toError(loadError).message);
      });
    return () => {
      current = false;
    };
  }, [eventId]);

  return (
    <Modal show onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>What can raise this{data === null ? '' : ` — ${data.event.name}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error !== null && <Alert variant="danger">{error}</Alert>}
        {error === null && data === null && <Spinner animation="border" size="sm" />}

        {data !== null && data.sources.length === 0 && (
          <p className="text-muted mb-0">Nothing raises this event automatically. It can only be raised by hand, from a card&apos;s Raise button.</p>
        )}

        {data !== null &&
          GROUP_ORDER.map((kind) => {
            const group = data.sources.filter((source) => source.kind === kind);
            if (group.length === 0) {
              return null;
            }
            return (
              // A div, not a <section>: the theme gives a bare section 3.5rem
              // of vertical padding, which is page-level spacing inside a modal.
              <div key={kind} className="sources-modal__group">
                <div className="sources-modal__heading">
                  <span className="sources-modal__dot" aria-hidden="true" />
                  {GROUP_TITLES[kind]}
                </div>
                <ul className="list-unstyled mb-0">
                  {group.map((source, index) => (
                    <SourceRow key={`${kind}-${index}`} source={source} eventZoneName={data.event.zone_name} />
                  ))}
                </ul>
              </div>
            );
          })}
      </Modal.Body>
    </Modal>
  );
};
